import { memo, useCallback, useContext, useEffect } from "react";
import { flushSync } from "react-dom";
import type { Blocker, History, Transition } from "history";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { ModalHeader } from "../ui/Modal";
import { useConfirmation, WrappedModalProps } from "./modals";

function useBlocker(blocker: Blocker, when = true): void {
  const navigationContextObject = useContext(UNSAFE_NavigationContext);

  const navigator = navigationContextObject
    .navigator as History;

  useEffect(() => {
    if (!when) return;

    const unblock = navigator.block((tx: Transition) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          // Automatically unblock the transition so it can play all the way
          // through before retrying it. TODO: Figure out how to re-enable
          // this block if the transition is cancelled for some reason.
          unblock();
          tx.retry();
        },
      };

      blocker(autoUnblockingTx);
    });

    return unblock;
  }, [ navigator, blocker, when ]);
}

export type NavigationConfirmationProps = WrappedModalProps & {
  enabled: boolean,
  onConfirm?: () => void,
}

export const NavigationConfirmation = memo<NavigationConfirmationProps>((
  {
    enabled,
    onConfirm,
    header = (
      <ModalHeader
        divider={false}
        text="Revert changes and leave page"
        supportingText={<pre>{"Warning this will reset all of your unsaved changes!\r\n Do you want to continue?"}</pre>}
        align="center"
      />
    ),
    ...props
  }) => {

  const { push } = useConfirmation();

  const blocker: Blocker = useCallback(
    (tx) => {
      push({
        messageKey: "NavigationConfirmation",
        header: header,
        onSubmit: () => {
          flushSync(() => onConfirm?.());
          tx.retry();
          return true;
        },
        ...props,
      });
    },
    [ header, onConfirm, props, push ],
  );

  useBlocker(blocker, enabled);
  return null;
});